import {
  getLessonStartupPerfNow,
  recordLessonStartupPerf,
  type LessonStartupPerfVariant,
} from "@/features/tutor/utils/lessonStartupPerf";

export type SlideHtmlCacheRecord = {
  html: string;
  sourceUrl: string;
  lessonId: unknown;
  slideIndex: unknown;
  lastFetchedAt: number;
};

export type SlideHtmlCacheState = {
  slideHtmlCache: Record<string, SlideHtmlCacheRecord>;
  slideHtmlInFlight: Record<string, Promise<string>>;
};

type StoreAccess = {
  get: () => SlideHtmlCacheState;
  set: (
    next:
      | Partial<SlideHtmlCacheState>
      | ((previous: SlideHtmlCacheState) => Partial<SlideHtmlCacheState>),
  ) => void;
};

type PerfRecorder = (
  event: string,
  data?: Record<string, unknown>,
  startedAt?: number,
) => void;

type CacheKeyInput = {
  lessonId: unknown;
  slideIndex: unknown;
  sourceUrl: string;
};

type LoadSlideHtmlInput = CacheKeyInput & {
  variant: LessonStartupPerfVariant;
  cacheEnabled: boolean;
  signal?: AbortSignal;
};

type PrefetchSlideHtmlInput = CacheKeyInput & {
  variant?: LessonStartupPerfVariant;
};

type FetchSlideHtml = (
  sourceUrl: string,
  signal?: AbortSignal,
) => Promise<string>;

type PrefetchSlideHtmlAssets = (html: string, sourceUrl: string) => void;

type SlideHtmlCacheControllerOptions = StoreAccess & {
  fetchSlideHtml?: FetchSlideHtml;
  prefetchSlideHtmlAssets?: PrefetchSlideHtmlAssets;
  now?: () => number;
  perfNow?: () => number;
  recordPerf?: PerfRecorder;
};

export function createSlideHtmlCacheState(): SlideHtmlCacheState {
  return {
    slideHtmlCache: {},
    slideHtmlInFlight: {},
  };
}

export function slideHtmlCacheKey(
  lessonId: unknown,
  slideIndex: unknown,
  sourceUrl: string,
): string {
  return `${lessonId ?? "unknown"}::${slideIndex ?? "unknown"}::${
    sourceUrl ?? ""
  }`;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

const missingFetchSlideHtml: FetchSlideHtml = async () => {
  throw new Error("fetchSlideHtml dependency was not provided");
};

const noopPrefetchSlideHtmlAssets: PrefetchSlideHtmlAssets = () => {};

function perfContext(
  input: CacheKeyInput,
  variant: LessonStartupPerfVariant,
  cacheEnabled?: boolean,
): Record<string, unknown> {
  return {
    lesson_id: input.lessonId,
    slide_index: input.slideIndex,
    variant,
    ...(typeof cacheEnabled === "boolean"
      ? { cache_enabled: cacheEnabled }
      : {}),
  };
}

export function createSlideHtmlCacheController({
  get,
  set,
  fetchSlideHtml = missingFetchSlideHtml,
  prefetchSlideHtmlAssets = noopPrefetchSlideHtmlAssets,
  now = Date.now,
  perfNow = getLessonStartupPerfNow,
  recordPerf = recordLessonStartupPerf,
}: SlideHtmlCacheControllerOptions) {
  const setCache = (
    lessonId: unknown,
    slideIndex: unknown,
    sourceUrl: string,
    html: string,
  ) => {
    const cacheKey = slideHtmlCacheKey(lessonId, slideIndex, sourceUrl);
    set((previous) => ({
      slideHtmlCache: {
        ...(previous.slideHtmlCache || {}),
        [cacheKey]: {
          html,
          sourceUrl,
          lessonId,
          slideIndex,
          lastFetchedAt: now(),
        },
      },
    }));
  };

  const getCached = (
    lessonId: unknown,
    slideIndex: unknown,
    sourceUrl: string,
  ): SlideHtmlCacheRecord | null => {
    const cacheRecord =
      get().slideHtmlCache?.[
        slideHtmlCacheKey(lessonId, slideIndex, sourceUrl)
      ];
    if (!cacheRecord || cacheRecord.sourceUrl !== sourceUrl) {
      return null;
    }
    return cacheRecord;
  };

  const setInFlight = (
    lessonId: unknown,
    slideIndex: unknown,
    sourceUrl: string,
    promise: Promise<string>,
  ) => {
    const cacheKey = slideHtmlCacheKey(lessonId, slideIndex, sourceUrl);
    set((previous) => ({
      slideHtmlInFlight: {
        ...(previous.slideHtmlInFlight || {}),
        [cacheKey]: promise,
      },
    }));
  };

  const getInFlight = (
    lessonId: unknown,
    slideIndex: unknown,
    sourceUrl: string,
  ): Promise<string> | null => {
    return (
      get().slideHtmlInFlight?.[
        slideHtmlCacheKey(lessonId, slideIndex, sourceUrl)
      ] ?? null
    );
  };

  const clearInFlight = (
    lessonId: unknown,
    slideIndex: unknown,
    sourceUrl: string,
    expectedPromise?: Promise<string>,
  ) => {
    const cacheKey = slideHtmlCacheKey(lessonId, slideIndex, sourceUrl);
    set((previous) => {
      const current = previous.slideHtmlInFlight?.[cacheKey];
      if (!current || (expectedPromise && current !== expectedPromise)) {
        return {};
      }
      const next = { ...(previous.slideHtmlInFlight || {}) };
      delete next[cacheKey];
      return { slideHtmlInFlight: next };
    });
  };

  const clearAll = () => {
    set(createSlideHtmlCacheState());
  };

  const loadSlideHtml = async (input: LoadSlideHtmlInput): Promise<string> => {
    const { lessonId, slideIndex, sourceUrl, variant, cacheEnabled, signal } =
      input;
    const context = perfContext(input, variant, cacheEnabled);

    if (!cacheEnabled) {
      const fetchStartedAt = perfNow();
      recordPerf("slide_html_fetch_begin", context);
      try {
        const html = await fetchSlideHtml(sourceUrl, signal);
        if (signal?.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }
        recordPerf("slide_html_fetch_success", context, fetchStartedAt);
        return html;
      } catch (error) {
        if (isAbortError(error)) {
          recordPerf("slide_html_fetch_aborted", context);
          throw error;
        }
        recordPerf(
          "slide_html_fetch_error",
          {
            ...context,
            message: error instanceof Error ? error.message : String(error),
          },
          fetchStartedAt,
        );
        throw error;
      }
    }

    const cached = getCached(lessonId, slideIndex, sourceUrl);
    if (cached?.html) {
      recordPerf("slide_html_cache_hit", context);
      return cached.html;
    }
    recordPerf("slide_html_cache_miss", context);

    const existingPromise = getInFlight(lessonId, slideIndex, sourceUrl);
    if (existingPromise) {
      recordPerf("slide_html_inflight_reuse", context);
      return existingPromise;
    }

    let sharedPromise!: Promise<string>;
    sharedPromise = (async () => {
      const fetchStartedAt = perfNow();
      recordPerf("slide_html_fetch_begin", context);
      try {
        const html = await fetchSlideHtml(sourceUrl);
        recordPerf("slide_html_fetch_success", context, fetchStartedAt);
        setCache(lessonId, slideIndex, sourceUrl, html);
        return html;
      } catch (error) {
        recordPerf(
          "slide_html_fetch_error",
          {
            ...context,
            message: error instanceof Error ? error.message : String(error),
          },
          fetchStartedAt,
        );
        throw error;
      } finally {
        clearInFlight(lessonId, slideIndex, sourceUrl, sharedPromise);
      }
    })();
    setInFlight(lessonId, slideIndex, sourceUrl, sharedPromise);
    return sharedPromise;
  };

  const prefetchSlideHtml = (
    input: PrefetchSlideHtmlInput,
  ): Promise<string> => {
    const variant = input.variant ?? "optimized";
    const context = perfContext(input, variant);
    const { lessonId, slideIndex, sourceUrl } = input;

    const cached = getCached(lessonId, slideIndex, sourceUrl);
    if (cached?.html) {
      recordPerf("slide_html_prefetch_cache_hit", context);
      return Promise.resolve(cached.html);
    }

    const existingPromise = getInFlight(lessonId, slideIndex, sourceUrl);
    if (existingPromise) {
      recordPerf("slide_html_prefetch_inflight_reuse", context);
      return existingPromise;
    }

    let sharedPromise!: Promise<string>;
    sharedPromise = (async () => {
      const prefetchStartedAt = perfNow();
      recordPerf("slide_html_prefetch_begin", context);
      try {
        const html = await fetchSlideHtml(sourceUrl);
        setCache(lessonId, slideIndex, sourceUrl, html);
        prefetchSlideHtmlAssets(html, sourceUrl);
        recordPerf("slide_html_prefetch_success", context, prefetchStartedAt);
        return html;
      } catch (error) {
        recordPerf(
          "slide_html_prefetch_error",
          {
            ...context,
            message: error instanceof Error ? error.message : String(error),
          },
          prefetchStartedAt,
        );
        throw error;
      } finally {
        clearInFlight(lessonId, slideIndex, sourceUrl, sharedPromise);
      }
    })();
    setInFlight(lessonId, slideIndex, sourceUrl, sharedPromise);
    void sharedPromise.catch(() => undefined);
    return sharedPromise;
  };

  return {
    setCache,
    getCached,
    setInFlight,
    getInFlight,
    clearInFlight,
    clearAll,
    loadSlideHtml,
    prefetchSlideHtml,
  };
}
